import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { useTrackImpressionEventByIntersection } from "shared/hooks";

import { PlusArchiveTitle } from "scmp-app/components/plus/archive/archive-title";
import { usePlusArticlePopup } from "scmp-app/components/plus/article-popup/hooks";
import { usePlusDetailedListingPopup } from "scmp-app/components/plus/detailed-listing-popup/hooks";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { interactivesPlusArchiveQuery$key } from "scmp-app/queries/__generated__/interactivesPlusArchiveQuery.graphql";

import { Container, ContentItemContainer, StyledContentItemPlusInteractives } from "./styles";

type Props = {
  className?: string;
  reference: interactivesPlusArchiveQuery$key;
};
export const PlusArchiveInteractives: FunctionComponent<Props> = ({ className, reference }) => {
  const content = useFragment(
    graphql`
      fragment interactivesPlusArchiveQuery on Query {
        plusArchiveInteractivesQueue: queue(filter: { name: "scmp_plus_interactives" }) {
          items(first: 3) {
            edges {
              node {
                ... on Content {
                  entityId
                  ...plusInteractivesContentItemContent
                  ...hooksPlusArticleLinkHandlerArticle
                }
              }
            }
          }
        }
      }
    `,
    reference,
  );

  const { handleOpen } = usePlusArticlePopup();
  const { handleOpen: handleOpenDetailedListingPopup } = usePlusDetailedListingPopup();
  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: () => ({
        action: "imp",
        category: "recirculation",
        customized_parameters: {
          list_of_articles: (
            content?.plusArchiveInteractivesQueue?.items?.edges?.map(edge => edge.node.entityId) ??
            []
          ).join(","),
          page_type: "archive",
          widget_name: "interactives",
        },
        subcategory: "widget",
      }),
      options: {
        isSendGA4Tracking: true,
        shouldSendOnce: true,
      },
    });

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "interactives",
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <PlusArchiveTitle
          onClick={() =>
            handleOpenDetailedListingPopup({
              pgtype: "archive",
              type: "interactives",
            })
          }
          title="Interactives"
        />
        <ContentItemContainer>
          {content?.plusArchiveInteractivesQueue?.items?.edges?.map(
            edge =>
              edge.node && (
                <StyledContentItemPlusInteractives
                  key={edge?.node?.entityId ?? ""}
                  onClick={() =>
                    handleOpen(edge.node, { pageType: "archive", widgetName: "interactives" })
                  }
                  reference={edge.node}
                />
              ),
          )}
        </ContentItemContainer>
      </Container>
    </BaseLinkContextProvider>
  );
};

PlusArchiveInteractives.displayName = "PlusArchiveInteractives";
